import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Underline as ULine, Strikethrough, Code, FileCode, List, ListOrdered, Heading1, Heading2, Heading3, Link as LinkIcon, Eraser, Quote, Minus, Undo, Redo } from "lucide-react";

/**
 * RichTextEditor — WYSIWYG TipTap.
 * Props:
 *   value: HTML string (controllato)
 *   onChange: (html:string) => void
 *   placeholder: stringa (opzionale)
 *   testIdPrefix: prefisso per data-testid (default 'rte')
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Scrivi qui...", testIdPrefix = "rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        codeBlock: { HTMLAttributes: { class: "rich-code-block" } },
        code: { HTMLAttributes: { class: "rich-code-inline" } },
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "tiptap prose prose-sm max-w-none focus:outline-none min-h-[160px] p-3",
        "data-testid": `${testIdPrefix}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap sputa "<p></p>" se vuoto — normalizza in stringa vuota
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Allinea il contenuto se il valore esterno cambia da fuori (es. reset form)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming || "", false);
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="rounded-2xl border border-outline bg-white min-h-[200px] flex items-center justify-center text-ink-500 text-sm">
        Caricamento editor…
      </div>
    );
  }

  const Btn = ({ active, onClick, title, children, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={`${testIdPrefix}-${tid}`}
      className={`px-2.5 py-1.5 rounded-lg font-bold text-sm transition-colors ${
        active ? "bg-gold text-ink-900" : "bg-white text-ink-700 hover:bg-gold-light"
      }`}
    >
      {children}
    </button>
  );

  const addLink = () => {
    const prev = editor.getAttributes("link")?.href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("URL del link (lascia vuoto per rimuovere):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    let href = url.trim();
    if (!/^(https?:|mailto:|tel:)/i.test(href)) href = `https://${href}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <div className="rounded-2xl border-2 border-outline bg-white overflow-hidden" data-testid={`${testIdPrefix}-wrapper`}>
      <div className="flex flex-wrap items-center gap-1 p-2 bg-parchment-dark border-b border-outline">
        <Btn tid="bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Grassetto">
          <Bold size={14}/>
        </Btn>
        <Btn tid="italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Corsivo">
          <Italic size={14}/>
        </Btn>
        <Btn tid="underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sottolineato">
          <ULine size={14}/>
        </Btn>
        <Btn tid="strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barrato">
          <Strikethrough size={14}/>
        </Btn>
        <div className="w-px h-6 bg-outline mx-1"/>
        <Btn tid="h1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titolo grande">
          <Heading1 size={14}/>
        </Btn>
        <Btn tid="h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titolo">
          <Heading2 size={14}/>
        </Btn>
        <Btn tid="h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Sottotitolo">
          <Heading3 size={14}/>
        </Btn>
        <Btn tid="quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citazione">
          <Quote size={14}/>
        </Btn>
        <div className="w-px h-6 bg-outline mx-1"/>
        <Btn tid="bullet" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Elenco puntato">
          <List size={14}/>
        </Btn>
        <Btn tid="ordered" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Elenco numerato">
          <ListOrdered size={14}/>
        </Btn>
        <Btn tid="hr" active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linea orizzontale">
          <Minus size={14}/>
        </Btn>
        <div className="w-px h-6 bg-outline mx-1"/>
        <Btn tid="code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()} title="Codice inline">
          <Code size={14}/>
        </Btn>
        <Btn tid="codeblock" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Blocco di codice">
          <FileCode size={14}/>
        </Btn>
        <Btn tid="link" active={editor.isActive("link")} onClick={addLink} title="Aggiungi link">
          <LinkIcon size={14}/>
        </Btn>
        <div className="w-px h-6 bg-outline mx-1"/>
        <Btn tid="undo" active={false} onClick={() => editor.chain().focus().undo().run()} title="Annulla">
          <Undo size={14}/>
        </Btn>
        <Btn tid="redo" active={false} onClick={() => editor.chain().focus().redo().run()} title="Ripeti">
          <Redo size={14}/>
        </Btn>
        <Btn tid="clear" active={false} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Rimuovi formattazione">
          <Eraser size={14}/>
        </Btn>
      </div>
      {!value && (
        <div className="absolute pointer-events-none text-ink-500 italic text-sm pl-3 pt-3" aria-hidden="true">
          {/* TipTap non ha placeholder nativo senza extension dedicata: questo è puramente decorativo, attivato condizionalmente */}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
